// Prompt 138V: every visible piece of UI copy on the public /register
// letter-first flow, in all four supported languages. Backend-supplied
// values (Agent display name/code, appName, intendedCountryCode/
// intendedLanguageCode, the server's own success/error message text) are
// NEVER looked up here - only this app's own UI copy is translated, and
// callers interpolate the raw backend value into the translated sentence
// (see the *(name) function entries below).
//
// LANGUAGES also drives the topbar's language chip labels - those stay in
// each language's own native name (never translated into e.g. "Thai" in
// English), matching how a real language switcher reads.
export const LANGUAGES = [
  { code: "th", label: "ไทย" },
  { code: "bn", label: "বাংলা" },
  { code: "lo", label: "ລາວ" },
  { code: "en", label: "EN" },
];

// Prompt 139B: the public register form's primary-skill dropdown options,
// in display order. "Other" always requires the companion free-text field.
//
// The backend (Prompt 139A, Services/Public/PrimarySkills.cs) only
// recognizes 5 of these as first-class codes: ConstructionWorker, Mason,
// Welder, FactoryWorker, Other - a deliberately closed set. The other 5
// (Electrician, Carpenter, Cleaner, Driver, GeneralWorker) exist only in
// this frontend option list; PublicInviteRegisterPage.jsx's
// resolveSkillSubmission() submits them as PrimarySkillCode="Other" +
// PrimarySkillOtherText=<English label> - the backend's own existing
// free-text path, so the real skill choice is still honestly persisted
// (never silently dropped or faked), just not as its own dedicated code
// yet. See that function's doc comment for the full reasoning.
export const SKILL_CODES = [
  "ConstructionWorker",
  "Mason",
  "Welder",
  "Electrician",
  "Carpenter",
  "FactoryWorker",
  "Cleaner",
  "Driver",
  "GeneralWorker",
  "Other",
];

export const BACKEND_KNOWN_SKILL_CODES = ["ConstructionWorker", "Mason", "Welder", "FactoryWorker", "Other"];

// English display name for every code - used as PrimarySkillOtherText when
// a UI-only code above is mapped to PrimarySkillCode="Other" for submit,
// so the persisted free text is always a clean, consistent value (never
// whatever language happened to be selected at the moment of submit).
export const SKILL_ENGLISH_LABELS = {
  ConstructionWorker: "Construction Worker",
  Mason: "Mason",
  Welder: "Welder",
  Electrician: "Electrician",
  Carpenter: "Carpenter",
  FactoryWorker: "Factory Worker",
  Cleaner: "Cleaner",
  Driver: "Driver",
  GeneralWorker: "General Worker",
  Other: "Other",
};

const TRANSLATIONS = {
  th: {
    brandSubtitle: "ระบบลงทะเบียนคำเชิญ",
    callAgent: "โทร Agent",

    envelopeEyebrow: "จดหมายเชิญสมัครแรงงาน Workmate X",
    envelopeFromAgent: (agentName) => `จดหมายจาก Agent ${agentName}`,
    envelopeHint: "แตะเพื่อเปิดจดหมายเชิญของคุณ",
    envelopeOpenCta: "เปิดจดหมาย",

    letterTitle: (agentName) => `คุณได้รับจดหมายเชิญจาก Agent ${agentName}`,
    letterDesc: (appName) =>
      `นี่คือเว็บลิงก์สมัครของ ${appName} ไม่ใช่แอปมือถือ อ่านรายละเอียดด้านล่างก่อนเริ่ม Quick Register`,
    explainLines: [
      "คุณได้รับคำเชิญให้เริ่มสมัครกับ Workmate X",
      "ขั้นตอนนี้คือ Quick Register",
      "หลังจากนี้ต้องทำ e-KYC",
      "ยังไม่ใช่การจับคู่งานหรือสัญญาจ้าง",
    ],
    contactFallbackLabel: "ผู้ติดต่อ",
    contactFallbackValue: 'โทรผ่านปุ่ม "โทร Agent" ด้านบน',
    contactTypeLabels: { Phone: "เบอร์โทร", WhatsApp: "WhatsApp", Line: "LINE" },
    targetGroupLabel: "กลุ่มเป้าหมาย",
    agentNoteLabel: "บันทึกจาก Agent",
    statusNote: "ใบสมัครนี้จะถูกส่งกลับไปหา Agent คนนี้เท่านั้น หากไม่มั่นใจให้โทรหา Agent ก่อนกรอกข้อมูล",
    benefitMessage: (appName) =>
      `${appName} ช่วยดูแลแรงงานที่มาทำงานในไทย ให้รู้ว่ามีใครรับผิดชอบ ติดต่อขอความช่วยเหลือได้ และติดตามขั้นตอนงานได้ชัดเจนขึ้น`,
    benefits: [
      { icon: "SOS", title: "ขอความช่วยเหลือ", desc: "มีช่องทางแจ้งเหตุและติดต่อทีมดูแล" },
      { icon: "A", title: "ล่ามและภาษา", desc: "ช่วยสื่อสารกับนายจ้างและทีมไทย" },
      { icon: "฿", title: "ส่งเงินกลับบ้าน", desc: "wallet และ remittance ในอนาคต" },
      { icon: "OK", title: "สิทธิ์และความปลอดภัย", desc: "ช่วยให้ทำงานในไทยอย่างมั่นใจขึ้น" },
      { icon: "J", title: "งานในไทย", desc: "ติดตามขั้นตอนหลัง match งาน" },
      { icon: "TEL", title: "Agent ดูแล", desc: "รู้ชัดว่าใครเป็นผู้รับผิดชอบคุณ" },
    ],
    startQuickRegister: "เริ่ม Quick Register",
    letterCtaNote: "ถ้ายังไม่พร้อม สามารถกลับมาเปิดลิงก์นี้อีกครั้งได้ หากคำเชิญยังไม่หมดอายุ",

    formSummaryLabel: "กำลังสมัครผ่านคำเชิญของ",
    backToLetter: "กลับไปดูจดหมาย",
    formStepLabel: "สมัครผ่านเว็บลิงก์นี้",
    formHeading: "กรอกข้อมูลสำคัญเท่านั้น",
    formSubheading: "ใช้เวลาประมาณ 2 นาที ข้อมูลอื่น Agent จะช่วยตรวจและกรอกเพิ่มภายหลัง",
    formTargetPrefix: "Agent ระบุกลุ่มเป้าหมายไว้ล่วงหน้า:",
    fieldFullName: "ชื่อ-นามสกุล",
    fieldFullNamePlaceholder: "เช่น Rahim Uddin",
    fieldPhone: "เบอร์โทร / WhatsApp",
    fieldPhonePlaceholder: "เช่น +880 1712 345678",
    fieldCountry: "ประเทศ",

    fieldSkill: "ความถนัด / Skill",
    skillLabels: {
      ConstructionWorker: "ช่างก่อสร้าง / Construction Worker",
      Mason: "ช่างปูน / Mason",
      Welder: "ช่างเชื่อม / Welder",
      Electrician: "ช่างไฟฟ้า / Electrician",
      Carpenter: "ช่างไม้ / Carpenter",
      FactoryWorker: "คนงานโรงงาน / Factory Worker",
      Cleaner: "แม่บ้าน / Cleaner",
      Driver: "คนขับรถ / Driver",
      GeneralWorker: "งานทั่วไป / General Worker",
      Other: "อื่นๆ / Other",
    },
    fieldSkillOtherPlaceholder: "โปรดระบุความถนัดของคุณ",
    errorSkillRequired: "กรุณาเลือกความถนัด / Skill",
    errorSkillOtherRequired: "กรุณาระบุความถนัดของคุณ",

    fieldPassportNumber: "หมายเลข Passport",
    fieldPassportNumberPlaceholder: "เช่น A1234567",
    errorPassportNumberRequired: "กรุณากรอกหมายเลข Passport",
    errorPassportNumberTooShort: "หมายเลข Passport สั้นเกินไป กรุณาตรวจสอบอีกครั้ง",

    fieldPassport: "รูปพาสปอร์ต / เอกสารประจำตัว",
    passportChoiceCameraButton: "ถ่ายรูปด้วยกล้อง",
    passportChoiceUploadButton: "อัปโหลดจากเครื่อง",
    passportChange: "เปลี่ยนรูป",
    passportRemove: "ลบรูป",
    passportRequiredNote: "ไฟล์นี้จะถูกส่งไปพร้อมใบสมัคร เพื่อให้ Agent และทีมกลางตรวจสอบ e-KYC",
    passportInputAriaLabel: "เลือกไฟล์รูปพาสปอร์ต",
    passportFileSizeLabel: "ขนาดไฟล์",
    errorPassportDocumentRequired: "กรุณาแนบรูปพาสปอร์ตหรือเอกสารประจำตัว",
    errorPassportDocumentTooLarge: "ไฟล์รูปมีขนาดใหญ่เกินไป (สูงสุด 8MB) กรุณาเลือกไฟล์อื่น",

    cameraCaptureCta: "ถ่ายรูป",
    cameraRetakeButton: "ถ่ายใหม่",
    cameraUsePhotoButton: "ใช้รูปนี้",
    cameraCloseButton: "ปิดกล้อง",
    cameraOpeningText: "กำลังเปิดกล้อง...",
    // Prompt 139B-R2: the exact required copy - shown both proactively (no
    // getUserMedia / insecure context, e.g. the live http:// test site) and
    // if a supported browser's getUserMedia() call itself still fails
    // (permission denied, no camera hardware, etc.) - one honest message,
    // never a dead button either way.
    cameraUnavailableNote: "ไม่สามารถเปิดกล้องจากเบราว์เซอร์นี้ได้ กรุณาอัปโหลดรูปจากเครื่องแทน",

    successSkillLine: "ส่งข้อมูล Skill แล้ว",
    successPassportLine: "ส่งหมายเลข Passport แล้ว",
    successDocumentLine: "ส่งรูปเอกสารแล้ว",

    errorFullNameRequired: "กรุณากรอกชื่อ-นามสกุล",
    errorPhoneRequired: "กรุณากรอกเบอร์โทร / WhatsApp",
    helpNote: (appName) => `ขั้นตอนนี้ยังไม่ใช่แอปมือถือ และยังไม่มีการเปิด tracking ใด ๆ เป็นเพียงใบสมัครผ่านเว็บลิงก์ ${appName}`,
    submitButton: (agentName) => `ส่งใบสมัครให้ Agent ${agentName}`,
    submitButtonSubmitting: "กำลังส่ง...",
    secondaryCallButton: "โทรหา Agent ก่อนสมัคร",
    submitGenericError: "ไม่สามารถส่งข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง",

    missingInviteTitle: "ไม่พบลิงก์คำเชิญ",
    missingInviteBody:
      "หน้านี้ใช้เปิดจากลิงก์คำเชิญที่ Agent ส่งให้เท่านั้น กรุณาเปิดผ่านลิงก์ที่ได้รับ หรือติดต่อ Agent ของคุณเพื่อขอลิงก์ใหม่",
    errorTitleNotFound: "ไม่พบลิงก์คำเชิญนี้",
    errorTitleGeneric: "เกิดข้อผิดพลาด",
    errorBodyNotFound: "ไม่พบลิงก์คำเชิญนี้ กรุณาตรวจสอบลิงก์อีกครั้งหรือติดต่อ Agent ของคุณ",
    errorBodyGeneric: "ไม่สามารถโหลดข้อมูลลิงก์นี้ได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง",
    unavailableTitle: "ลิงก์นี้ใช้งานไม่ได้แล้ว",
    unavailableReasons: {
      Revoked: "ลิงก์นี้ถูกเพิกถอนแล้ว กรุณาติดต่อ Agent ของคุณเพื่อขอลิงก์ใหม่",
      Expired: "ลิงก์นี้หมดอายุแล้ว กรุณาติดต่อ Agent ของคุณเพื่อขอลิงก์ใหม่",
      MaxUsesReached: "ลิงก์นี้ถูกใช้ครบจำนวนแล้ว กรุณาติดต่อ Agent ของคุณเพื่อขอลิงก์ใหม่",
      NotActive: "ลิงก์นี้ยังไม่พร้อมใช้งาน กรุณาติดต่อ Agent ของคุณ",
    },
    unavailableDefault: "ลิงก์นี้ไม่สามารถใช้งานได้แล้ว กรุณาติดต่อ Agent ของคุณเพื่อขอลิงก์ใหม่",

    successTitle: "Quick Register สำเร็จ",
    successDuplicateTitle: "เราได้รับใบสมัครนี้แล้ว",
    successAgentWillContact: (agentName) => `Agent ${agentName} จะติดต่อกลับ`,
    successEkycNote: "ขั้นตอนถัดไปคือ e-KYC ซึ่งจำเป็นก่อนที่คุณจะพร้อม match งาน ยังไม่พร้อม match งานจนกว่าจะผ่าน e-KYC",

    loadingText: "กำลังโหลดจดหมายเชิญของคุณจาก Workmate X...",
  },

  en: {
    brandSubtitle: "Invite Card Register",
    callAgent: "Call Agent",

    envelopeEyebrow: "Workmate X Worker Invitation Letter",
    envelopeFromAgent: (agentName) => `A letter from Agent ${agentName}`,
    envelopeHint: "Tap to open your invitation letter",
    envelopeOpenCta: "Open Letter",

    letterTitle: (agentName) => `You've received an invitation letter from Agent ${agentName}`,
    letterDesc: (appName) =>
      `This is ${appName}'s web registration link, not a mobile app. Please read the details below before starting Quick Register.`,
    explainLines: [
      "You have been invited to start registering with Workmate X",
      "This step is Quick Register",
      "e-KYC verification is required after this",
      "This is not a job match or an employment contract yet",
    ],
    contactFallbackLabel: "Contact",
    contactFallbackValue: 'Call using the "Call Agent" button above',
    contactTypeLabels: { Phone: "Phone", WhatsApp: "WhatsApp", Line: "LINE" },
    targetGroupLabel: "Target Group",
    agentNoteLabel: "Note from Agent",
    statusNote: "This application is sent only to this Agent. If you're unsure, call the Agent before filling in your details.",
    benefitMessage: (appName) =>
      `${appName} looks after workers coming to work in Thailand - so you always know who is responsible for you, can reach out for help, and can track your progress clearly.`,
    benefits: [
      { icon: "SOS", title: "Get Help", desc: "A channel to report issues and reach the care team" },
      { icon: "A", title: "Interpreter & Language", desc: "Help communicating with employers and the Thai team" },
      { icon: "฿", title: "Send Money Home", desc: "Wallet and remittance, coming soon" },
      { icon: "OK", title: "Rights & Safety", desc: "Helps you work in Thailand with confidence" },
      { icon: "J", title: "Jobs in Thailand", desc: "Track your progress after a job match" },
      { icon: "TEL", title: "Agent Support", desc: "Always clear who is responsible for you" },
    ],
    startQuickRegister: "Start Quick Register",
    letterCtaNote: "Not ready yet? You can come back and open this link again while the invite is still valid.",

    formSummaryLabel: "Registering with the invitation from",
    backToLetter: "Back to letter",
    formStepLabel: "Registering via this web link",
    formHeading: "Fill in only the essentials",
    formSubheading: "Takes about 2 minutes. Your Agent will help review and complete the rest later.",
    formTargetPrefix: "Agent's pre-defined target group:",
    fieldFullName: "Full name",
    fieldFullNamePlaceholder: "e.g. Rahim Uddin",
    fieldPhone: "Phone / WhatsApp",
    fieldPhonePlaceholder: "e.g. +880 1712 345678",
    fieldCountry: "Country",

    fieldSkill: "Skill",
    skillLabels: {
      ConstructionWorker: "Construction Worker",
      Mason: "Mason",
      Welder: "Welder",
      Electrician: "Electrician",
      Carpenter: "Carpenter",
      FactoryWorker: "Factory Worker",
      Cleaner: "Cleaner",
      Driver: "Driver",
      GeneralWorker: "General Worker",
      Other: "Other",
    },
    fieldSkillOtherPlaceholder: "Please describe your skill",
    errorSkillRequired: "Please select a skill",
    errorSkillOtherRequired: "Please describe your skill",

    fieldPassportNumber: "Passport Number",
    fieldPassportNumberPlaceholder: "e.g. A1234567",
    errorPassportNumberRequired: "Please enter your passport number",
    errorPassportNumberTooShort: "That passport number looks too short - please check it again",

    fieldPassport: "Passport photo / ID document",
    passportChoiceCameraButton: "Take a photo with camera",
    passportChoiceUploadButton: "Upload from device",
    passportChange: "Change photo",
    passportRemove: "Remove photo",
    passportRequiredNote: "This file will be sent with your application, so your Agent and the central e-KYC team can review it.",
    passportInputAriaLabel: "Choose a passport image",
    passportFileSizeLabel: "File size",
    errorPassportDocumentRequired: "Please attach a passport photo or ID document",
    errorPassportDocumentTooLarge: "That image is too large (8MB max). Please choose a different file.",

    cameraCaptureCta: "Capture",
    cameraRetakeButton: "Retake",
    cameraUsePhotoButton: "Use this photo",
    cameraCloseButton: "Close camera",
    cameraOpeningText: "Opening camera...",
    cameraUnavailableNote: "Unable to open the camera from this browser. Please upload a photo from your device instead.",

    successSkillLine: "Skill submitted",
    successPassportLine: "Passport number submitted",
    successDocumentLine: "Document photo submitted",

    errorFullNameRequired: "Please enter your full name",
    errorPhoneRequired: "Please enter your phone / WhatsApp number",
    helpNote: (appName) => `This step is not a mobile app, and no tracking is active yet. It is only an application submitted via ${appName}'s web link.`,
    submitButton: (agentName) => `Send application to Agent ${agentName}`,
    submitButtonSubmitting: "Sending...",
    secondaryCallButton: "Call Agent before applying",
    submitGenericError: "Unable to submit right now. Please try again.",

    missingInviteTitle: "No invitation link found",
    missingInviteBody:
      "This page can only be opened from an invitation link sent by an Agent. Please open the link you received, or contact your Agent for a new one.",
    errorTitleNotFound: "This invitation link was not found",
    errorTitleGeneric: "Something went wrong",
    errorBodyNotFound: "This invitation link was not found. Please check the link again or contact your Agent.",
    errorBodyGeneric: "Unable to load this link right now. Please try again.",
    unavailableTitle: "This link is no longer usable",
    unavailableReasons: {
      Revoked: "This link has been revoked. Please contact your Agent for a new one.",
      Expired: "This link has expired. Please contact your Agent for a new one.",
      MaxUsesReached: "This link has reached its usage limit. Please contact your Agent for a new one.",
      NotActive: "This link is not active yet. Please contact your Agent.",
    },
    unavailableDefault: "This link can no longer be used. Please contact your Agent for a new one.",

    successTitle: "Quick Register successful",
    successDuplicateTitle: "We already received this application",
    successAgentWillContact: (agentName) => `Agent ${agentName} will contact you`,
    successEkycNote: "Next is e-KYC, which is required before you're ready for job matching. You are not yet ready for job matching until e-KYC is complete.",

    loadingText: "Loading your invitation letter from Workmate X...",
  },

  bn: {
    brandSubtitle: "আমন্ত্রণ কার্ড নিবন্ধন",
    callAgent: "এজেন্টকে কল করুন",

    envelopeEyebrow: "Workmate X শ্রমিক আমন্ত্রণপত্র",
    envelopeFromAgent: (agentName) => `এজেন্ট ${agentName} এর পাঠানো চিঠি`,
    envelopeHint: "আপনার আমন্ত্রণপত্র খুলতে চাপুন",
    envelopeOpenCta: "চিঠি খুলুন",

    letterTitle: (agentName) => `আপনি এজেন্ট ${agentName} এর কাছ থেকে একটি আমন্ত্রণপত্র পেয়েছেন`,
    letterDesc: (appName) =>
      `এটি ${appName} এর ওয়েব নিবন্ধন লিংক, কোনো মোবাইল অ্যাপ নয়। Quick Register শুরু করার আগে নিচের বিস্তারিত পড়ুন।`,
    explainLines: [
      "আপনাকে Workmate X এ নিবন্ধন শুরু করার জন্য আমন্ত্রণ জানানো হয়েছে",
      "এই ধাপটি হলো Quick Register",
      "এরপর e-KYC সম্পন্ন করতে হবে",
      "এটি এখনো কোনো চাকরি ম্যাচ বা চুক্তি নয়",
    ],
    contactFallbackLabel: "যোগাযোগ",
    contactFallbackValue: 'উপরের "এজেন্টকে কল করুন" বাটন দিয়ে কল করুন',
    contactTypeLabels: { Phone: "ফোন", WhatsApp: "WhatsApp", Line: "LINE" },
    targetGroupLabel: "লক্ষ্য গোষ্ঠী",
    agentNoteLabel: "এজেন্টের নোট",
    statusNote: "এই আবেদনটি শুধুমাত্র এই এজেন্টের কাছে পাঠানো হবে। নিশ্চিত না হলে তথ্য দেওয়ার আগে এজেন্টকে কল করুন।",
    benefitMessage: (appName) =>
      `${appName} থাইল্যান্ডে কাজ করতে আসা শ্রমিকদের যত্ন নিতে সাহায্য করে — কে দায়িত্বে আছে তা জানা, সাহায্যের জন্য যোগাযোগ করা এবং কাজের অগ্রগতি স্পষ্টভাবে ট্র্যাক করা যায়।`,
    benefits: [
      { icon: "SOS", title: "সাহায্য নিন", desc: "ঘটনা জানানো ও কেয়ার টিমের সাথে যোগাযোগের মাধ্যম" },
      { icon: "A", title: "দোভাষী ও ভাষা", desc: "নিয়োগকর্তা ও থাই টিমের সাথে যোগাযোগে সহায়তা" },
      { icon: "৳", title: "দেশে টাকা পাঠান", desc: "ভবিষ্যতে ওয়ালেট ও রেমিট্যান্স সুবিধা" },
      { icon: "OK", title: "অধিকার ও নিরাপত্তা", desc: "থাইল্যান্ডে নিশ্চিন্তে কাজ করতে সাহায্য করে" },
      { icon: "J", title: "থাইল্যান্ডে কাজ", desc: "ম্যাচের পর অগ্রগতি ট্র্যাক করুন" },
      { icon: "TEL", title: "এজেন্ট সহায়তা", desc: "সবসময় স্পষ্ট থাকে কে দায়িত্বে আছে" },
    ],
    startQuickRegister: "Quick Register শুরু করুন",
    letterCtaNote: "এখনো প্রস্তুত নন? আমন্ত্রণ মেয়াদ থাকা পর্যন্ত এই লিংকটি আবার খুলতে পারবেন।",

    formSummaryLabel: "যার আমন্ত্রণে আবেদন করছেন",
    backToLetter: "চিঠিতে ফিরে যান",
    formStepLabel: "এই ওয়েব লিংকের মাধ্যমে নিবন্ধন",
    formHeading: "শুধুমাত্র প্রয়োজনীয় তথ্য পূরণ করুন",
    formSubheading: "প্রায় ২ মিনিট সময় লাগবে। বাকি তথ্য এজেন্ট পরে যাচাই ও পূরণ করতে সাহায্য করবেন।",
    formTargetPrefix: "এজেন্টের নির্ধারিত লক্ষ্য গোষ্ঠী:",
    fieldFullName: "পূর্ণ নাম",
    fieldFullNamePlaceholder: "যেমন Rahim Uddin",
    fieldPhone: "ফোন / WhatsApp",
    fieldPhonePlaceholder: "যেমন +880 1712 345678",
    fieldCountry: "দেশ",

    fieldSkill: "দক্ষতা",
    skillLabels: {
      ConstructionWorker: "নির্মাণ শ্রমিক",
      Mason: "রাজমিস্ত্রি",
      Welder: "ওয়েল্ডার",
      Electrician: "ইলেকট্রিশিয়ান",
      Carpenter: "ছুতার",
      FactoryWorker: "কারখানা শ্রমিক",
      Cleaner: "পরিচ্ছন্নতাকর্মী",
      Driver: "ড্রাইভার",
      GeneralWorker: "সাধারণ কাজ",
      Other: "অন্যান্য",
    },
    fieldSkillOtherPlaceholder: "আপনার দক্ষতা লিখুন",
    errorSkillRequired: "অনুগ্রহ করে একটি দক্ষতা নির্বাচন করুন",
    errorSkillOtherRequired: "অনুগ্রহ করে আপনার দক্ষতা লিখুন",

    fieldPassportNumber: "পাসপোর্ট নম্বর",
    fieldPassportNumberPlaceholder: "যেমন A1234567",
    errorPassportNumberRequired: "অনুগ্রহ করে আপনার পাসপোর্ট নম্বর লিখুন",
    errorPassportNumberTooShort: "পাসপোর্ট নম্বরটি খুব ছোট মনে হচ্ছে — আবার যাচাই করুন",

    fieldPassport: "পাসপোর্ট ছবি / পরিচয়পত্র",
    passportChoiceCameraButton: "ক্যামেরা দিয়ে ছবি তুলুন",
    passportChoiceUploadButton: "ডিভাইস থেকে আপলোড করুন",
    passportChange: "ছবি পরিবর্তন করুন",
    passportRemove: "ছবি সরান",
    passportRequiredNote: "এই ফাইলটি আপনার আবেদনের সাথে পাঠানো হবে, যাতে এজেন্ট ও কেন্দ্রীয় e-KYC টিম যাচাই করতে পারে।",
    passportInputAriaLabel: "পাসপোর্ট ছবি বাছাই করুন",
    passportFileSizeLabel: "ফাইলের আকার",
    errorPassportDocumentRequired: "অনুগ্রহ করে পাসপোর্ট ছবি বা পরিচয়পত্র সংযুক্ত করুন",
    errorPassportDocumentTooLarge: "ছবিটি অনেক বড় (সর্বোচ্চ 8MB)। অনুগ্রহ করে অন্য ফাইল বেছে নিন।",

    cameraCaptureCta: "ছবি তুলুন",
    cameraRetakeButton: "আবার তুলুন",
    cameraUsePhotoButton: "এই ছবিটি ব্যবহার করুন",
    cameraCloseButton: "ক্যামেরা বন্ধ করুন",
    cameraOpeningText: "ক্যামেরা খোলা হচ্ছে...",
    cameraUnavailableNote: "এই ব্রাউজার থেকে ক্যামেরা খোলা যাচ্ছে না। অনুগ্রহ করে আপনার ডিভাইস থেকে একটি ছবি আপলোড করুন।",

    successSkillLine: "দক্ষতার তথ্য পাঠানো হয়েছে",
    successPassportLine: "পাসপোর্ট নম্বর পাঠানো হয়েছে",
    successDocumentLine: "নথির ছবি পাঠানো হয়েছে",

    errorFullNameRequired: "অনুগ্রহ করে আপনার পূর্ণ নাম লিখুন",
    errorPhoneRequired: "অনুগ্রহ করে আপনার ফোন / WhatsApp নম্বর লিখুন",
    helpNote: (appName) => `এই ধাপটি কোনো মোবাইল অ্যাপ নয়, এবং এখনো কোনো ট্র্যাকিং চালু নেই। এটি শুধুমাত্র ${appName} এর ওয়েব লিংকের মাধ্যমে একটি আবেদন।`,
    submitButton: (agentName) => `এজেন্ট ${agentName} এর কাছে আবেদন পাঠান`,
    submitButtonSubmitting: "পাঠানো হচ্ছে...",
    secondaryCallButton: "আবেদনের আগে এজেন্টকে কল করুন",
    submitGenericError: "এই মুহূর্তে পাঠানো সম্ভব হচ্ছে না। আবার চেষ্টা করুন।",

    missingInviteTitle: "কোনো আমন্ত্রণ লিংক পাওয়া যায়নি",
    missingInviteBody:
      "এই পাতাটি শুধুমাত্র এজেন্টের পাঠানো আমন্ত্রণ লিংক থেকে খোলা যায়। অনুগ্রহ করে আপনার পাওয়া লিংক দিয়ে খুলুন, অথবা নতুন লিংকের জন্য আপনার এজেন্টের সাথে যোগাযোগ করুন।",
    errorTitleNotFound: "এই আমন্ত্রণ লিংকটি পাওয়া যায়নি",
    errorTitleGeneric: "একটি সমস্যা হয়েছে",
    errorBodyNotFound: "এই আমন্ত্রণ লিংকটি পাওয়া যায়নি। অনুগ্রহ করে লিংকটি আবার যাচাই করুন অথবা আপনার এজেন্টের সাথে যোগাযোগ করুন।",
    errorBodyGeneric: "এই মুহূর্তে এই লিংকের তথ্য লোড করা যাচ্ছে না। আবার চেষ্টা করুন।",
    unavailableTitle: "এই লিংকটি আর ব্যবহারযোগ্য নয়",
    unavailableReasons: {
      Revoked: "এই লিংকটি বাতিল করা হয়েছে। নতুন লিংকের জন্য আপনার এজেন্টের সাথে যোগাযোগ করুন।",
      Expired: "এই লিংকের মেয়াদ শেষ হয়ে গেছে। নতুন লিংকের জন্য আপনার এজেন্টের সাথে যোগাযোগ করুন।",
      MaxUsesReached: "এই লিংকটি সর্বোচ্চ ব্যবহার সীমায় পৌঁছেছে। নতুন লিংকের জন্য আপনার এজেন্টের সাথে যোগাযোগ করুন।",
      NotActive: "এই লিংকটি এখনো সক্রিয় নয়। আপনার এজেন্টের সাথে যোগাযোগ করুন।",
    },
    unavailableDefault: "এই লিংকটি আর ব্যবহার করা যাবে না। নতুন লিংকের জন্য আপনার এজেন্টের সাথে যোগাযোগ করুন।",

    successTitle: "Quick Register সফল হয়েছে",
    successDuplicateTitle: "আমরা ইতিমধ্যে এই আবেদনটি পেয়েছি",
    successAgentWillContact: (agentName) => `এজেন্ট ${agentName} আপনার সাথে যোগাযোগ করবেন`,
    successEkycNote: "পরবর্তী ধাপ হলো e-KYC, যা কাজ ম্যাচের জন্য প্রস্তুত হওয়ার আগে প্রয়োজন। e-KYC সম্পন্ন না হওয়া পর্যন্ত আপনি কাজ ম্যাচের জন্য প্রস্তুত নন।",

    loadingText: "Workmate X থেকে আপনার আমন্ত্রণপত্র লোড হচ্ছে...",
  },

  lo: {
    brandSubtitle: "ລົງທະບຽນບັດເຊີນ",
    callAgent: "ໂທຫາ Agent",

    envelopeEyebrow: "ຈົດໝາຍເຊີນສະໝັກແຮງງານ Workmate X",
    envelopeFromAgent: (agentName) => `ຈົດໝາຍຈາກ Agent ${agentName}`,
    envelopeHint: "ແຕະເພື່ອເປີດຈົດໝາຍເຊີນຂອງທ່ານ",
    envelopeOpenCta: "ເປີດຈົດໝາຍ",

    letterTitle: (agentName) => `ທ່ານໄດ້ຮັບຈົດໝາຍເຊີນຈາກ Agent ${agentName}`,
    letterDesc: (appName) =>
      `ນີ້ແມ່ນລິງເວັບສະໝັກຂອງ ${appName} ບໍ່ແມ່ນແອັບມືຖື ກະລຸນາອ່ານລາຍລະອຽດຂ້າງລຸ່ມກ່ອນເລີ່ມ Quick Register`,
    explainLines: [
      "ທ່ານໄດ້ຮັບການເຊີນໃຫ້ເລີ່ມສະໝັກກັບ Workmate X",
      "ຂັ້ນຕອນນີ້ຄື Quick Register",
      "ຫຼັງຈາກນີ້ຕ້ອງເຮັດ e-KYC",
      "ຍັງບໍ່ແມ່ນການຈັບຄູ່ວຽກ ຫຼື ສັນຍາຈ້າງ",
    ],
    contactFallbackLabel: "ຜູ້ຕິດຕໍ່",
    contactFallbackValue: 'ໂທຜ່ານປຸ່ມ "ໂທຫາ Agent" ດ້ານເທິງ',
    contactTypeLabels: { Phone: "ເບີໂທ", WhatsApp: "WhatsApp", Line: "LINE" },
    targetGroupLabel: "ກຸ່ມເປົ້າໝາຍ",
    agentNoteLabel: "ບັນທຶກຈາກ Agent",
    statusNote: "ໃບສະໝັກນີ້ຈະຖືກສົ່ງກັບໄປຫາ Agent ຄົນນີ້ເທົ່ານັ້ນ ຖ້າບໍ່ແນ່ໃຈໃຫ້ໂທຫາ Agent ກ່ອນປ້ອນຂໍ້ມູນ",
    benefitMessage: (appName) =>
      `${appName} ຊ່ວຍເບິ່ງແຍງແຮງງານທີ່ມາເຮັດວຽກຢູ່ໄທ ໃຫ້ຮູ້ວ່າມີໃຜຮັບຜິດຊອບ ຕິດຕໍ່ຂໍຄວາມຊ່ວຍເຫຼືອໄດ້ ແລະ ຕິດຕາມຂັ້ນຕອນວຽກໄດ້ຊັດເຈນຂຶ້ນ`,
    benefits: [
      { icon: "SOS", title: "ຂໍຄວາມຊ່ວຍເຫຼືອ", desc: "ມີຊ່ອງທາງແຈ້ງເຫດ ແລະ ຕິດຕໍ່ທີມດູແລ" },
      { icon: "A", title: "ນາຍພາສາ ແລະ ພາສາ", desc: "ຊ່ວຍສື່ສານກັບນາຍຈ້າງ ແລະ ທີມໄທ" },
      { icon: "฿", title: "ສົ່ງເງິນກັບບ້ານ", desc: "wallet ແລະ remittance ໃນອະນາຄົດ" },
      { icon: "OK", title: "ສິດ ແລະ ຄວາມປອດໄພ", desc: "ຊ່ວຍໃຫ້ເຮັດວຽກຢູ່ໄທຢ່າງໝັ້ນໃຈຂຶ້ນ" },
      { icon: "J", title: "ວຽກຢູ່ໄທ", desc: "ຕິດຕາມຂັ້ນຕອນຫຼັງຈັບຄູ່ວຽກ" },
      { icon: "TEL", title: "Agent ດູແລ", desc: "ຮູ້ຊັດວ່າໃຜເປັນຜູ້ຮັບຜິດຊອບທ່ານ" },
    ],
    startQuickRegister: "ເລີ່ມ Quick Register",
    letterCtaNote: "ຖ້າຍັງບໍ່ພ້ອມ ສາມາດກັບມາເປີດລິງນີ້ອີກຄັ້ງໄດ້ ຖ້າຄຳເຊີນຍັງບໍ່ໝົດອາຍຸ",

    formSummaryLabel: "ກຳລັງສະໝັກຜ່ານຄຳເຊີນຂອງ",
    backToLetter: "ກັບໄປເບິ່ງຈົດໝາຍ",
    formStepLabel: "ສະໝັກຜ່ານລິງເວັບນີ້",
    formHeading: "ປ້ອນຂໍ້ມູນສຳຄັນເທົ່ານັ້ນ",
    formSubheading: "ໃຊ້ເວລາປະມານ 2 ນາທີ ຂໍ້ມູນອື່ນ Agent ຈະຊ່ວຍກວດ ແລະ ປ້ອນເພີ່ມພາຍຫຼັງ",
    formTargetPrefix: "Agent ລະບຸກຸ່ມເປົ້າໝາຍໄວ້ລ່ວງໜ້າ:",
    fieldFullName: "ຊື່-ນາມສະກຸນ",
    fieldFullNamePlaceholder: "ເຊັ່ນ Rahim Uddin",
    fieldPhone: "ເບີໂທ / WhatsApp",
    fieldPhonePlaceholder: "ເຊັ່ນ +880 1712 345678",
    fieldCountry: "ປະເທດ",

    fieldSkill: "ຄວາມຊຳນານ",
    skillLabels: {
      ConstructionWorker: "ຄົນງານກໍ່ສ້າງ",
      Mason: "ຊ່າງປູນ",
      Welder: "ຊ່າງເຊື່ອມ",
      Electrician: "ຊ່າງໄຟຟ້າ",
      Carpenter: "ຊ່າງໄມ້",
      FactoryWorker: "ຄົນງານໂຮງງານ",
      Cleaner: "ແມ່ບ້ານ",
      Driver: "ຄົນຂັບລົດ",
      GeneralWorker: "ວຽກທົ່ວໄປ",
      Other: "ອື່ນໆ",
    },
    fieldSkillOtherPlaceholder: "ກະລຸນາລະບຸຄວາມຊຳນານຂອງທ່ານ",
    errorSkillRequired: "ກະລຸນາເລືອກຄວາມຊຳນານ",
    errorSkillOtherRequired: "ກະລຸນາລະບຸຄວາມຊຳນານຂອງທ່ານ",

    fieldPassportNumber: "ເລກທີ່ Passport",
    fieldPassportNumberPlaceholder: "ເຊັ່ນ A1234567",
    errorPassportNumberRequired: "ກະລຸນາປ້ອນເລກທີ່ Passport",
    errorPassportNumberTooShort: "ເລກທີ່ Passport ສັ້ນເກີນໄປ ກະລຸນາກວດສອບອີກຄັ້ງ",

    fieldPassport: "ຮູບພາສປອດ / ເອກະສານປະຈຳຕົວ",
    passportChoiceCameraButton: "ຖ່າຍຮູບດ້ວຍກ້ອງ",
    passportChoiceUploadButton: "ອັບໂຫລດຈາກເຄື່ອງ",
    passportChange: "ປ່ຽນຮູບ",
    passportRemove: "ລຶບຮູບ",
    passportRequiredNote: "ໄຟລ໌ນີ້ຈະຖືກສົ່ງໄປພ້ອມໃບສະໝັກ ເພື່ອໃຫ້ Agent ແລະ ທີມກາງກວດສອບ e-KYC",
    passportInputAriaLabel: "ເລືອກໄຟລ໌ຮູບພາສປອດ",
    passportFileSizeLabel: "ຂະໜາດໄຟລ໌",
    errorPassportDocumentRequired: "ກະລຸນາແນບຮູບພາສປອດ ຫຼື ເອກະສານປະຈຳຕົວ",
    errorPassportDocumentTooLarge: "ຮູບໃຫຍ່ເກີນໄປ (ສູງສຸດ 8MB) ກະລຸນາເລືອກໄຟລ໌ອື່ນ",

    cameraCaptureCta: "ຖ່າຍຮູບ",
    cameraRetakeButton: "ຖ່າຍໃໝ່",
    cameraUsePhotoButton: "ໃຊ້ຮູບນີ້",
    cameraCloseButton: "ປິດກ້ອງ",
    cameraOpeningText: "ກຳລັງເປີດກ້ອງ...",
    cameraUnavailableNote: "ບໍ່ສາມາດເປີດກ້ອງຈາກເບົາເຊີນີ້ໄດ້ ກະລຸນາອັບໂຫລດຮູບຈາກເຄື່ອງແທນ",

    successSkillLine: "ສົ່ງຂໍ້ມູນຄວາມຊຳນານແລ້ວ",
    successPassportLine: "ສົ່ງເລກທີ່ Passport ແລ້ວ",
    successDocumentLine: "ສົ່ງຮູບເອກະສານແລ້ວ",

    errorFullNameRequired: "ກະລຸນາປ້ອນຊື່-ນາມສະກຸນ",
    errorPhoneRequired: "ກະລຸນາປ້ອນເບີໂທ / WhatsApp",
    helpNote: (appName) => `ຂັ້ນຕອນນີ້ຍັງບໍ່ແມ່ນແອັບມືຖື ແລະ ຍັງບໍ່ມີການເປີດ tracking ໃດໆ ເປັນພຽງໃບສະໝັກຜ່ານລິງເວັບ ${appName}`,
    submitButton: (agentName) => `ສົ່ງໃບສະໝັກໃຫ້ Agent ${agentName}`,
    submitButtonSubmitting: "ກຳລັງສົ່ງ...",
    secondaryCallButton: "ໂທຫາ Agent ກ່ອນສະໝັກ",
    submitGenericError: "ບໍ່ສາມາດສົ່ງຂໍ້ມູນໄດ້ໃນຂະນະນີ້ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",

    missingInviteTitle: "ບໍ່ພົບລິງຄຳເຊີນ",
    missingInviteBody:
      "ໜ້ານີ້ໃຊ້ເປີດຈາກລິງຄຳເຊີນທີ່ Agent ສົ່ງໃຫ້ເທົ່ານັ້ນ ກະລຸນາເປີດຜ່ານລິງທີ່ໄດ້ຮັບ ຫຼື ຕິດຕໍ່ Agent ຂອງທ່ານເພື່ອຂໍລິງໃໝ່",
    errorTitleNotFound: "ບໍ່ພົບລິງຄຳເຊີນນີ້",
    errorTitleGeneric: "ເກີດຂໍ້ຜິດພາດ",
    errorBodyNotFound: "ບໍ່ພົບລິງຄຳເຊີນນີ້ ກະລຸນາກວດສອບລິງອີກຄັ້ງ ຫຼື ຕິດຕໍ່ Agent ຂອງທ່ານ",
    errorBodyGeneric: "ບໍ່ສາມາດໂຫຼດຂໍ້ມູນລິງນີ້ໄດ້ໃນຂະນະນີ້ ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
    unavailableTitle: "ລິງນີ້ໃຊ້ງານບໍ່ໄດ້ແລ້ວ",
    unavailableReasons: {
      Revoked: "ລິງນີ້ຖືກເພິກຖອນແລ້ວ ກະລຸນາຕິດຕໍ່ Agent ຂອງທ່ານເພື່ອຂໍລິງໃໝ່",
      Expired: "ລິງນີ້ໝົດອາຍຸແລ້ວ ກະລຸນາຕິດຕໍ່ Agent ຂອງທ່ານເພື່ອຂໍລິງໃໝ່",
      MaxUsesReached: "ລິງນີ້ຖືກໃຊ້ຄົບຈຳນວນແລ້ວ ກະລຸນາຕິດຕໍ່ Agent ຂອງທ່ານເພື່ອຂໍລິງໃໝ່",
      NotActive: "ລິງນີ້ຍັງບໍ່ພ້ອມໃຊ້ງານ ກະລຸນາຕິດຕໍ່ Agent ຂອງທ່ານ",
    },
    unavailableDefault: "ລິງນີ້ບໍ່ສາມາດໃຊ້ງານໄດ້ແລ້ວ ກະລຸນາຕິດຕໍ່ Agent ຂອງທ່ານເພື່ອຂໍລິງໃໝ່",

    successTitle: "Quick Register ສຳເລັດ",
    successDuplicateTitle: "ພວກເຮົາໄດ້ຮັບໃບສະໝັກນີ້ແລ້ວ",
    successAgentWillContact: (agentName) => `Agent ${agentName} ຈະຕິດຕໍ່ກັບ`,
    successEkycNote: "ຂັ້ນຕອນຕໍ່ໄປຄື e-KYC ເຊິ່ງຈຳເປັນກ່ອນທີ່ທ່ານຈະພ້ອມ match ວຽກ ຍັງບໍ່ພ້ອມ match ວຽກຈົນກວ່າຈະຜ່ານ e-KYC",

    loadingText: "ກຳລັງໂຫຼດຈົດໝາຍເຊີນຂອງທ່ານຈາກ Workmate X...",
  },
};

/** Returns the translation dictionary for `language`, falling back to Thai for an unknown/unset code. */
export function getPublicInviteText(language) {
  return TRANSLATIONS[language] ?? TRANSLATIONS.th;
}

/**
 * Best-effort mapping from the backend's own free-text
 * intendedLanguageCode/intendedCountryCode (never translated, never
 * normalized on the backend) to one of our four supported UI language
 * codes - used only to pick a sensible default selected language, never to
 * alter what's displayed about the invite itself.
 */
export function inferDefaultLanguage(card) {
  const lang = (card?.intendedLanguageCode || "").trim().toLowerCase();
  const country = (card?.intendedCountryCode || "").trim().toUpperCase();

  if (lang.startsWith("beng") || lang.startsWith("bang") || lang === "bn" || country === "BD") {
    return "bn";
  }
  if (lang.startsWith("lao") || lang === "lo" || country === "LA") {
    return "lo";
  }
  if (lang.startsWith("eng") || lang === "en") {
    return "en";
  }
  if (lang.startsWith("thai") || lang === "th" || country === "TH") {
    return "th";
  }
  return null;
}
